// src/components/onboarding/steps/Step3AIIdentity.tsx
import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch'; // Import Switch
import InfoModal from '@/components/ui/InfoModal'; // Import InfoModal
import { OnboardingStepProps } from '@/types/onboarding';
import { isValidAreaCode, isValidE164 } from '@/lib/validators';
import VoiceSelectionCard, { VoiceOption } from '@/components/onboarding/VoiceSelectionCard';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabaseClient';
import { Loader2, Check, X } from 'lucide-react';
import { aiVoiceOptions } from '@/pages/dashboard/receptionist-settings/settingsConstants';


const Step3AIIdentity: React.FC<OnboardingStepProps> = ({ data, updateData, goToNextStep, goToPrevStep }) => {
  const [errors, setErrors] = useState<{ voice?: string; areaCode?: string; forwardingNumber?: string; hipaa?: string; replyToEmail?: string }>({});
  const [forwardingInput, setForwardingInput] = useState(data.forwardingNumber?.replace(/^\+1/, '') ?? '');
  const playingAudioRef = useRef<HTMLAudioElement | null>(null);

  const handleVoiceSelect = (voice: VoiceOption) => {
    if (playingAudioRef.current) {
      playingAudioRef.current.pause();
      playingAudioRef.current = null;
    }
    updateData({
      aiVoiceId: voice.id,
      aiName: voice.name,
      aiVoiceProvider: voice.provider
    });
    setErrors(prev => ({ ...prev, voice: undefined }));
  };

  const handlePlaySample = (voice: VoiceOption) => {
    const sampleUrl = voice.sampleUrl;
    if (!sampleUrl) {
      toast.error("No audio sample available for this voice.");
      return;
    }
    if (playingAudioRef.current) {
      playingAudioRef.current.pause();
      playingAudioRef.current.currentTime = 0;
      playingAudioRef.current = null;
    }
    console.log(`Attempting to play sample for ${voice.name} (URL: ${sampleUrl})`);
    try {
      const audio = new Audio(sampleUrl);
      playingAudioRef.current = audio;
      audio.play()
        .then(() => {
          console.log(`Playing audio: ${sampleUrl}`);
          toast.info(`Playing sample for ${voice.name}...`);
        })
        .catch(err => {
          console.error("Error playing audio:", err);
          toast.error(`Could not play audio sample. Please try again.`);
          playingAudioRef.current = null;
        });
      audio.onended = () => { playingAudioRef.current = null; };
      audio.onerror = (e) => {
        console.error("Audio loading error:", e);
        toast.error(`Failed to load audio for ${voice.name}.`);
        playingAudioRef.current = null;
      }
    } catch (err) {
      console.error("Error creating Audio object:", err);
      toast.error(`Failed to load audio sample.`);
      playingAudioRef.current = null;
    }
  };

  const handleAreaCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newAreaCode = e.target.value.replace(/\D/g,'').slice(0,3);
    updateData({
        areaCode: newAreaCode,
        isAreaCodeAvailable: null,
        areaCodeCheckStatus: 'idle',
    });
    if (errors.areaCode) {
        setErrors(prev => ({ ...prev, areaCode: undefined }));
    }
  }

  const handleCheckAvailability = async () => {
    if (!isValidAreaCode(data.areaCode)) {
      setErrors(prev => ({ ...prev, areaCode: 'Please enter a valid 3-digit area code.'}));
      return;
    }
    setErrors(prev => ({ ...prev, areaCode: undefined }));
    updateData({ areaCodeCheckStatus: 'checking', isAreaCodeAvailable: null });

    try {
      const { data: result, error } = await supabase.functions.invoke('check-twilio-availability', {
        body: { areaCode: data.areaCode },
      });

      if (error) {
          console.error("Error checking availability:", error);
          throw new Error(error.message || 'Failed to check availability.');
      }

      console.log("Availability check result:", result);
      if (typeof result.available === 'boolean') {
          updateData({
              isAreaCodeAvailable: result.available,
              areaCodeCheckStatus: 'checked'
          });
          if (!result.available) {
              toast.error(`No phone numbers currently available for area code ${data.areaCode}.`);
              setErrors(prev => ({...prev, areaCode: `Area code ${data.areaCode} unavailable. Try another.`}))
          } else {
               toast.success(`Area code ${data.areaCode} is available!`);
          }
      } else {
           throw new Error("Invalid response from availability check.");
      }

    } catch (err: unknown) {
      console.error("Catch block - Error checking availability:", err);
      const message = err instanceof Error ? err.message : String(err);
      toast.error(`Error: ${message}`);
      updateData({ isAreaCodeAvailable: false, areaCodeCheckStatus: 'checked' });
      setErrors(prev => ({...prev, areaCode: `Failed to check ${data.areaCode}. Try again.`}))
    }
  };

  const handleForwardingInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '');
    setForwardingInput(digits.slice(0, 10));
    if (errors.forwardingNumber) {
      setErrors(prev => ({ ...prev, forwardingNumber: undefined }));
    }
  };

  const normalizeAndStoreForwardingNumber = (input: string): string => {
    const digits = input.replace(/\D/g, '');
    let e164Number = '';
    if (digits.length === 10) {
      e164Number = `+1${digits}`;
    } else if (digits.length === 11 && digits.startsWith('1')) {
      e164Number = `+${digits}`;
    } else if (input.startsWith('+') && isValidE164(input)) {
      e164Number = input;
    }
    updateData({ forwardingNumber: input ? e164Number : '' });
    return e164Number;
  }

  const validateAndProceed = () => {
    if (playingAudioRef.current) {
      playingAudioRef.current.pause();
      playingAudioRef.current = null;
    }

    const newErrors: typeof errors = {};
    if (!data.aiVoiceId) {
      newErrors.voice = 'Please select an AI voice.';
    }
    if (!data.areaCode) {
      newErrors.areaCode = 'Area code is required.';
    } else if (!isValidAreaCode(data.areaCode)) {
      newErrors.areaCode = 'Please enter a valid 3-digit area code.';
    } else if (data.areaCodeCheckStatus !== 'checked') {
      newErrors.areaCode = 'Please check availability for the area code first.';
    } else if (data.isAreaCodeAvailable !== true) {
      newErrors.areaCode = `Area code ${data.areaCode} is unavailable or check failed. Try another.`;
    }

    const normalizedNumber = normalizeAndStoreForwardingNumber(forwardingInput);
    if (forwardingInput && !normalizedNumber) {
      newErrors.forwardingNumber = 'Please enter a valid 10-digit US number.';
    } else if (normalizedNumber && !isValidE164(normalizedNumber)) {
      newErrors.forwardingNumber = 'Invalid phone number format.';
    }

    // Validate Reply-To Email (required)
    const email = data.replyToEmail;
    if (!email || !email.trim()) {
      newErrors.replyToEmail = 'Reply-To Email is required.';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        newErrors.replyToEmail = 'Enter a valid email address.';
      }
    }

    // HIPAA doesn't strictly need validation here as it's a boolean toggle
    // but if it were a required choice with an unselected state, you'd add:
    // if (data.hipaa_enabled === undefined) { newErrors.hipaa = 'Please make a selection for HIPAA compliance.'}

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      goToNextStep();
    }
  };

  useEffect(() => {
    return () => {
      if (playingAudioRef.current) {
        playingAudioRef.current.pause();
        playingAudioRef.current = null;
      }
    };
  }, []);

  return (
    <div>
      <h2 className="text-2xl font-semibold mb-6 text-gray-800 dark:text-gray-100">Choose your AI's voice & number.</h2>

      <div className="space-y-8">
        <div>
          <Label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-2">
            Select your AI's voice <span className="text-red-500">*</span>
          </Label>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {aiVoiceOptions.map((voice) => (
              <VoiceSelectionCard
                key={voice.id}
                voice={voice}
                isSelected={data.aiVoiceId === voice.id}
                onSelect={handleVoiceSelect}
                onPlaySample={handlePlaySample}
              />
            ))}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">This is the voice your callers will hear. Click "Play Sample" for a preview.</p>
          {errors.voice && <p className="text-xs text-red-600 mt-1">{errors.voice}</p>}
        </div>

        <div>
          <Label htmlFor="areaCode" className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Primary business area code <span className="text-red-500">*</span>
          </Label>
          <div className="flex items-start gap-2 mt-1">
            <div className="flex-grow">
                <Input
                    id="areaCode"
                    type="tel"
                    value={data.areaCode}
                    onChange={handleAreaCodeChange}
                    placeholder="e.g., 305"
                    maxLength={3}
                    className={`w-full ${errors.areaCode ? 'border-red-500' : ''} ${data.isAreaCodeAvailable === true ? 'border-green-500' : data.isAreaCodeAvailable === false ? 'border-red-500' : ''}`}
                    required
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Enter the main 3-digit area code (e.g., 212, 415). We'll use this to assign a local number.
                </p>
                {errors.areaCode && <p className="text-xs text-red-600 mt-1">{errors.areaCode}</p>}
                 {data.areaCodeCheckStatus === 'checked' && (
                    <div className={`flex items-center text-xs mt-1 ${data.isAreaCodeAvailable ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                        {data.isAreaCodeAvailable ? <Check size={14} className="mr-1" /> : <X size={14} className="mr-1" />}
                        {data.isAreaCodeAvailable ? `Area code ${data.areaCode} available!` : `Area code ${data.areaCode} unavailable or check failed.`}
                    </div>
                )}
            </div>
             <Button
                type="button"
                variant="outline"
                onClick={handleCheckAvailability}
                disabled={data.areaCodeCheckStatus === 'checking' || !isValidAreaCode(data.areaCode)}
                className="flex-shrink-0 mt-0"
            >
                {data.areaCodeCheckStatus === 'checking' ? (
                    <Loader2 size={16} className="animate-spin mr-2" />
                ) : null}
                {data.areaCodeCheckStatus === 'checking' ? 'Checking...' : 'Check Availability'}
            </Button>
          </div>
        </div>
        
        {/* HIPAA Compliance Section */}
        <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-md bg-gray-50 dark:bg-gray-800/30">
            <div className="flex items-center justify-between">
                <div className="flex items-center">
                    <Label htmlFor="hipaa_enabled" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Enable HIPAA Compliance Mode?
                    </Label>
                    <InfoModal
                        title="HIPAA Compliance Mode"
                        description="Enable this if your business handles Protected Health Information (PHI) and needs to comply with HIPAA. When active, call recordings, transcript, and outcomes are disabled."
                        triggerClassName="ml-2"
                    />
                </div>
                <Switch
                    id="hipaa_enabled"
                    checked={data.hipaa_enabled}
                    onCheckedChange={(checked) => updateData({ hipaa_enabled: checked })}
                    aria-label="Toggle HIPAA Compliance Mode"
                />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                If enabled, call scripts and data handling will be adjusted for HIPAA. Call recordings, transcript, and outcomes will be disabled.
            </p>
            {errors.hipaa && <p className="text-xs text-red-600 mt-1">{errors.hipaa}</p>}
        </div>


        <div>
          <Label htmlFor="forwardingNumber" className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Backup phone number (human transfer) <span className="text-gray-500 text-xs">(Optional)</span>
          </Label>
          <Input
            id="forwardingNumber"
            type="tel"
            value={forwardingInput}
            onChange={handleForwardingInputChange}
            placeholder="5551234567"
            maxLength={10}
            className={`mt-1 w-full md:w-1/2 ${errors.forwardingNumber ? 'border-red-500' : ''}`}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            If a caller asks for a person, calls go here. Enter a 10-digit number; we'll format it.
          </p>
          {errors.forwardingNumber && <p className="text-xs text-red-600 mt-1">{errors.forwardingNumber}</p>}
        </div>

        <div>
          <Label htmlFor="replyToEmail" className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Reply-To email (your inbox) <span className="text-red-500">*</span>
          </Label>
          <Input
            id="replyToEmail"
            type="email"
            value={data.replyToEmail || ''}
            onChange={(e) => updateData({ replyToEmail: e.target.value })}
            placeholder="you@example.com"
            className={`mt-1 w-full md:w-1/2 ${errors.replyToEmail ? 'border-red-500' : ''}`}
            required
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Replies to AI emails go to this address.</p>
          {errors.replyToEmail && <p className="text-xs text-red-600 mt-1">{errors.replyToEmail}</p>}
        </div>
      </div>

      <div className="mt-10 flex justify-between">
        <Button variant="outline" onClick={goToPrevStep} disabled={!goToPrevStep || data.areaCodeCheckStatus === 'checking'}>
          Back
        </Button>
        <Button onClick={validateAndProceed} disabled={data.areaCodeCheckStatus === 'checking'}>Next</Button>
      </div>
    </div>
  );
};

export default Step3AIIdentity;